import {ReactNode, useCallback, useEffect, useRef} from 'react';

import useDarkMode from '@/shared/hooks/useDarkMode';

interface Props {
  children: ReactNode;
}

const PoketmonTemplate = ({children}: Props) => {
  const {isDarkMode} = useDarkMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const touchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!containerRef.current || !overlayRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const x = e.touches[0].clientX - centerX;
    const y = e.touches[0].clientY - centerY;

    // 회전 각도를 제한
    const rotateY = Math.min(Math.max((x / (rect.width / 2)) * 35, -35), 35);
    const rotateX = Math.min(Math.max(-(y / (rect.height / 2)) * 35, -35), 35);

    // 배경 위치와 gradient 위치 조정
    const moveX = (x / rect.width) * 100;
    const moveY = (y / rect.height) * 100;

    if (!containerRef.current || !overlayRef.current) return;

    overlayRef.current.style.transition = 'background-position 0.1s, filter 0.1s';
    containerRef.current.style.transition = 'transform 0.1s';

    // gradient 위치를 0~100% 사이로 제한
    overlayRef.current.style.backgroundPosition = `${Math.max(Math.min(50 + moveX, 100), 0)}% ${Math.max(
      Math.min(50 + moveY, 100),
      0
    )}%`;
    overlayRef.current.style.filter = 'brightness(1.1) opacity(0.8)';

    containerRef.current.style.transform = `
        perspective(1000px)
        rotateX(${rotateX}deg)
        rotateY(${rotateY}deg)
        scale3d(1.02, 1.02, 1.02)
      `;
  }, []);

  const touchEnd = useCallback(() => {
    if (!containerRef.current || !overlayRef.current) return;

    containerRef.current.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    overlayRef.current.style.transition =
      'background-position 0.3s cubic-bezier(0.4, 0, 0.2, 1), filter 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

    requestAnimationFrame(() => {
      if (!containerRef.current || !overlayRef.current) return;

      containerRef.current.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
      overlayRef.current.style.backgroundPosition = '100%';
      overlayRef.current.style.filter = 'brightness(1.1) opacity(0)';
    });

    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.style.transition = '';
      }
      if (overlayRef.current) {
        overlayRef.current.style.transition = '';
      }
    }, 300);
  }, []);

  useEffect(() => {
    const krewIdElement = document.querySelector('.tit_card');
    if (!krewIdElement) return;
    krewIdElement.classList.add('gradient-text');
  }, []);

  return (
    <>
      <div onTouchMove={touchMove} onTouchEnd={touchEnd} ref={containerRef}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `
              repeating-linear-gradient(
                130deg,
                transparent 40%,
                rgba(255, 255, 255, 0.5) 50%,
                rgba(255, 255, 255, 0.5) 50%,
                transparent 65%
              )`,
            filter: 'brightness(1.1) opacity(0)',
            mixBlendMode: 'soft-light',
            backgroundSize: '150% 150%',
            backgroundPosition: '100%',
            zIndex: 1000,
            pointerEvents: 'none',
            borderRadius: '16px',
            boxShadow: isDarkMode ? '0 0 10px 0 rgba(255, 255, 255, 0.5)' : '0 0 10px 0 rgba(0, 0, 0, 0.5)'
          }}
          ref={overlayRef}
        ></div>
        {children}
      </div>
      <style>
        {`
          .gradient-text {
            position: relative;
          }

          .gradient-text::after {
            content: attr(data-text);
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            z-index: 1;

            background: linear-gradient(90deg, transparent 40%, #f1f1f1 50%, transparent 60%) -100% / 200%;
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            animation: shimmer 3s linear infinite;
          }

          @keyframes shimmer { to { background-position: 100% } }
        `}
      </style>
    </>
  );
};
export default PoketmonTemplate;
